/**
 * Floor operations the clerk does DURING a sale, as pure functions so they
 * are testable without a browser or a ring:
 *
 *  - paddle lookup (§11.2): the clerk's primary key for a human. A bidder may
 *    hold an online paddle (assigned at registration, high range) and a live
 *    paddle (handed out at check-in, low range); either resolves to them.
 *  - on-the-fly lot (§11.3): a horse added at the ring. Validated here before
 *    it becomes a createLot call - hip numbers are unique within a sale, and
 *    the run-order slot defaults to "after everything" so it doesn't jump the
 *    queue.
 *
 * The DOM layer (main.ts) calls these, renders the result, and only then
 * touches control-api / the ring.
 *
 * Anything here that names the catalog number to a person takes the sale's
 * `LotWords` (lot-term.ts) as an argument, with no default: a caller cannot
 * forget it and say "hip" on a LotNumber sale.
 */

import type { LotWords } from './lot-term.js';

export interface PaddleReg {
  id: string;
  accountId: string;
  onlinePaddle: number | null;
  livePaddle: number | null;
  approvalStatus: string;
  bidLimitCents: number | null;
  /** Who holds the paddle. Optional: a registration can exist before anyone
   *  has typed a name, and the clerk still needs to find it. */
  bidderName?: string | null;
  bidderPhone?: string | null;
  /** ADR-70 cut 3: the id the ring knows this bidder by. Absent from an
   *  older API. */
  ringBidderId?: string | null;
}

/**
 * ADR-70 cut 3 (defect 6): does this registration hold the high bid? An
 * online high bid names its bidder on the floor snapshot (highBidderId); a
 * floor bid names a paddle only when the clerk took one. The public label
 * ("Online" / "Floor") identifies nobody, which is why the old comparison
 * never lit a row.
 */
export function holdsHighBid(reg: PaddleReg, high: { highBidderId: string | null; highPaddle: string | null; highIsFloor: boolean }): boolean {
  if (high.highIsFloor) {
    if (high.highPaddle === null) return false;
    const n = Number(high.highPaddle);
    return Number.isInteger(n) && (reg.livePaddle === n || reg.onlinePaddle === n);
  }
  return high.highBidderId !== null && reg.ringBidderId != null && reg.ringBidderId === high.highBidderId;
}

export interface PaddleMatch {
  reg: PaddleReg;
  which: 'live' | 'online';
  /** A bidder can only bid if APPROVED - surfaced so the clerk sees it. */
  canBid: boolean;
}

/** Registrations whose live OR online paddle equals this number. Live paddle
 *  wins when a reg matches on both (the physical paddle in the room). */
export function findByPaddle(regs: readonly PaddleReg[], paddle: number): PaddleMatch[] {
  const out: PaddleMatch[] = [];
  for (const reg of regs) {
    const which = reg.livePaddle === paddle ? 'live' : reg.onlinePaddle === paddle ? 'online' : null;
    if (which) out.push({ reg, which, canBid: reg.approvalStatus === 'APPROVED' });
  }
  return out;
}

/** ADR-70 cut 6c: the ring's most recent pass or no sale that a reopen can
 *  bring back (the floor snapshot's `reopenable`, auction-core
 *  reopenableDeparture). */
export interface Reopenable { readonly lotId: string; readonly outcome: 'PASSED' | 'NO_SALE' }

/** Read `reopenable` off a floor snapshot; null when absent or malformed. */
export function reopenableOf(v: unknown): Reopenable | null {
  if (v === null || typeof v !== 'object') return null;
  const r = v as { lotId?: unknown; outcome?: unknown };
  return typeof r.lotId === 'string' && (r.outcome === 'PASSED' || r.outcome === 'NO_SALE') ? { lotId: r.lotId, outcome: r.outcome } : null;
}

/**
 * Whether the Reopen control may be offered.
 *
 * Mirrors the reducer: REOPEN_LOT is legal from HAMMERED_PENDING (ADR-42 D2)
 * and, since ADR-70 cut 6c, for the ring's most recent pass or no sale while
 * the lot in the ring holds no bid - which the ring itself works out and puts
 * on the floor snapshot (`reopenable`), so this is never a second copy of
 * that rule. A committed SOLD is still a settlement fact: it is voided or
 * turned back (ADR-65), never reopened. The server is authoritative either
 * way - this keeps the UI from inviting an action that always fails.
 */
export function reopenAllowed(lotState: string | null | undefined, reopenable: Reopenable | null = null): boolean {
  return lotState === 'HAMMERED_PENDING' || reopenable !== null;
}

/** The lot a Reopen press names: the hammered lot, else the reopenable one. */
export function reopenTarget(snap: { readonly lotId: string | null; readonly lotState?: string | null } | null, reopenable: Reopenable | null): { lotId: string; from: 'HAMMERED' | 'PASSED' | 'NO_SALE' } | null {
  if (snap?.lotState === 'HAMMERED_PENDING' && snap.lotId) return { lotId: snap.lotId, from: 'HAMMERED' };
  return reopenable ? { lotId: reopenable.lotId, from: reopenable.outcome } : null;
}

/**
 * Pricing (§11.5) now lives in @hipnumber/auction-core so the console board, the
 * Smart Buyer Card, and future settlement compute per-head/choice/per-lot the
 * SAME way. Re-exported here for the existing console importers.
 */
export { pricingView, type PriceView } from '@hipnumber/auction-core';

export interface NewLotInput {
  hipNumber: string;
  title: string;
  kind: string;
  openingAskDollars?: number;
  /** The seller's reserve, entered in dollars like the opening ask. */
  reserveDollars?: number;
  pricingMode?: string;
  quantity?: number;
  /** The seller the lot is attributed to; left out, the lot has none (the office sets it later). */
  consignorId?: string;
}

export type NewLotResult =
  | { ok: false; error: string }
  | {
      ok: true;
      /** The createLot request body (cents at the boundary; runOrder appended). */
      body: { hipNumber: string; title: string; kind: string; runOrder: number; openingAskCents?: number; reserveCents?: number; pricingMode?: 'PER_LOT' | 'PER_HEAD' | 'CHOICE'; quantity?: number; consignorId?: string };
    };

const PRICING_MODES = ['PER_LOT', 'PER_HEAD', 'CHOICE'] as const;
type PricingMode = (typeof PRICING_MODES)[number];

/**
 * Validate an on-the-fly lot against the sale it joins.
 *   - hip and title are required and trimmed
 *   - hip must be unique within the sale (case-insensitive; auctioneers write
 *     "12A")
 *   - kind must be one the platform knows (checked against the caller's list)
 *   - run order slots after the current max, so the new lot runs last unless
 *     the clerk reorders it
 */
export function validateNewLot(
  input: NewLotInput,
  existing: readonly { hipNumber: string; runOrder: number | null }[],
  kinds: readonly string[],
  words: LotWords,
): NewLotResult {
  const hipNumber = input.hipNumber.trim();
  const title = input.title.trim();
  if (!hipNumber) return { ok: false, error: `${words.low} number is required` };
  if (!title) return { ok: false, error: 'title is required' };
  if (!kinds.includes(input.kind)) return { ok: false, error: `unknown kind "${input.kind}"` };
  if (existing.some((l) => l.hipNumber.toLowerCase() === hipNumber.toLowerCase())) {
    return { ok: false, error: `${words.low} ${hipNumber} already exists in this sale` };
  }
  if (input.openingAskDollars !== undefined && (!Number.isFinite(input.openingAskDollars) || input.openingAskDollars < 0)) {
    return { ok: false, error: 'opening ask must be a non-negative number' };
  }
  if (input.reserveDollars !== undefined && (!Number.isFinite(input.reserveDollars) || input.reserveDollars < 0)) {
    return { ok: false, error: 'reserve must be a non-negative number' };
  }
  const pricingMode = (input.pricingMode ?? 'PER_LOT') as PricingMode;
  if (!PRICING_MODES.includes(pricingMode)) return { ok: false, error: `unknown pricing mode "${input.pricingMode}"` };
  const quantity = input.quantity ?? 1;
  if (!Number.isInteger(quantity) || quantity < 1) return { ok: false, error: 'quantity must be a positive integer' };
  if (pricingMode === 'PER_LOT' && quantity !== 1) return { ok: false, error: 'per-lot pricing is one item; use per-head or choice for a group' };
  const maxRun = existing.reduce((m, l) => Math.max(m, l.runOrder ?? 0), 0);
  return {
    ok: true,
    body: {
      hipNumber,
      title,
      kind: input.kind,
      runOrder: maxRun + 1,
      ...(input.openingAskDollars !== undefined ? { openingAskCents: Math.round(input.openingAskDollars * 100) } : {}),
      ...(input.reserveDollars !== undefined ? { reserveCents: Math.round(input.reserveDollars * 100) } : {}),
      ...(pricingMode !== 'PER_LOT' ? { pricingMode, quantity } : {}),
      ...(input.consignorId ? { consignorId: input.consignorId } : {}),
    },
  };
}

/**
 * The number the next lot added at the ring should carry: the largest numeric
 * part of any number in the sale, plus one ("12A" counts as 12, so the next is
 * 13). The office's Add form prefills the same way (web-admin nextHip), so the
 * two screens agree; the clerk can still type anything, and validateNewLot
 * refuses a number that is taken.
 */
export function nextLotNumber(existing: readonly { hipNumber: string }[]): string {
  let max = 0;
  for (const l of existing) {
    const n = Number.parseInt((l.hipNumber ?? '').replace(/\D/g, ''), 10);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return String(max + 1);
}

/**
 * Dollars typed into a box, as a number: "$2,500" and "2500" both read 2500, a
 * blank box reads "not given". Anything else is an error the dialog shows next
 * to the box, never a silent zero.
 *
 * A comma is accepted only as a thousands separator ("1,250" and "12,500.50"),
 * never as a decimal point or a stray key: "1250,50" and "2,50" are an error,
 * not 125,050 and 250. A bare "$" is an error too, not "no amount".
 */
export function parseDollars(text: string): { ok: true; value: number | undefined } | { ok: false } {
  const trimmed = text.trim();
  if (trimmed === '') return { ok: true, value: undefined };
  const t = trimmed.replace(/^\$\s*/, '');
  if (!/^(\d+|\d{1,3}(,\d{3})+)(\.\d{1,2})?$/.test(t)) return { ok: false };
  return { ok: true, value: Number(t.replace(/,/g, '')) };
}

/**
 * What the clerk typed for a bidder's bid limit, as cents for the wire. Currency
 * FORMATTING ($, thousands commas) is stripped - not [^0-9.], which would also
 * strip a leading "-" and turn a typo'd negative amount into a positive one.
 * The ring bids in whole dollars, so the limit is judged as typed BEFORE it is
 * turned into cents: 0.999 rounds to 100 cents, and judging the rounded value
 * would let a sub-dollar limit through as $1.
 */
export function bidLimitCents(text: string): { ok: true; cents: number } | { ok: false; error: string } {
  const dollars = Number(text.trim().replace(/[$,]/g, ''));
  if (!Number.isFinite(dollars) || dollars <= 0) return { ok: false, error: 'bid limit must be a positive amount' };
  if (dollars < 1) return { ok: false, error: 'bid limit must be at least $1 - leave it blank for no limit' };
  return { ok: true, cents: Math.round(dollars * 100) };
}

/** The increment at an amount - mirrors auction-core's default-v1 table. */
export function incrementAt(amount: number): number {
  return amount < 10_000 ? 500 : amount < 50_000 ? 1_000 : amount < 100_000 ? 2_500 : 5_000;
}

/**
 * What a lot opens at, given only what the catalog says.
 *
 * The catalogued ask when the sale office set one. Otherwise the SMALLEST
 * increment, and that choice is deliberate rather than a round number pulled
 * from the air: OPEN_LOT is the moment dormant pre-bid maxes run (ADR-42 §3),
 * and a proxy bids up FROM the opening ask. Opening a horse that carries a
 * $5,000 max at a guessed $1,000 charges that bidder $1,000 for nothing.
 * Opening low costs nobody anything - the auctioneer's next breath asks more.
 *
 * So the fallback errs downward on purpose. A too-low ask is a sentence the
 * auctioneer fixes in a second; a too-high one is money out of a bidder's
 * pocket that no one in the ring can see happening.
 */
export function openingAskFor(lot: { openingAskCents: number | null } | undefined): number {
  if (lot?.openingAskCents != null && lot.openingAskCents > 0) return Math.round(lot.openingAskCents / 100);
  return incrementAt(0);
}

/**
 * A lot the ring can still sell, and the next horse to run after a given one
 * per the AUTHORITATIVE run order (never "the next hip number").
 *
 * ADR-64: the rule lives in auction-core (`progression.ts`) and is the same
 * one Auction TV, Auction Progress and the GFX composition use, so the clerk's
 * Next button and the venue screens can never name different horses. It skips
 * scratched horses, already-disposed ones (sold / no sale / passed) and
 * `notice` slots; `draft` stays sellable because a horse added at the ring is
 * created as a draft and has to be sellable within seconds.
 */
export { sellable, nextInRunOrder } from '@hipnumber/auction-core';

/** Digits only, for comparing anything a human might type as a phone number. */
const digitsOf = (s: string): string => s.replace(/\D/g, '');

/**
 * Find a bidder the way a clerk actually asks for one.
 *
 * At a ring the identifier is whatever the person in front of you can produce:
 * a paddle they are holding, a name they say, a phone number the office has on
 * file. Making the clerk know WHICH of those the system indexes on is the
 * software's problem leaking out, so one box takes all three.
 *
 * A paddle match is EXACT and wins - "247" means paddle 247, not every phone
 * number containing 247. Phone matching needs at least four digits so a stray
 * "12" does not return the whole sale.
 */
export function findBidders(regs: readonly PaddleReg[], query: string): PaddleReg[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const digits = digitsOf(q);
  const asPaddle = digits.length > 0 && digits === q ? Number(digits) : null;

  const byPaddle = asPaddle === null ? [] : regs.filter((r) => r.onlinePaddle === asPaddle || r.livePaddle === asPaddle);
  if (byPaddle.length > 0) return byPaddle;

  return regs.filter((r) => {
    if (r.bidderName && r.bidderName.toLowerCase().includes(q)) return true;
    if (digits.length >= 4 && r.bidderPhone && digitsOf(r.bidderPhone).includes(digits)) return true;
    return false;
  });
}

/**
 * The last four of a phone number, for a screen other people can see.
 *
 * A sale office is a public room. The office needs enough to confirm they have
 * the right buyer and to go look the number up; it does not need every full
 * phone number on the catalog sitting on a monitor by the door.
 */
export function phoneTail(phone: string | null | undefined): string | null {
  const d = digitsOf(phone ?? '');
  return d.length >= 4 ? d.slice(-4) : null;
}

export interface SaleCheckInput {
  /** The hammer price about to be committed, in dollars. */
  price: number;
  /** What the board said the high bid was. */
  currentHighBid: number | null;
  /** The paddle typed at "number, please?", if any. */
  paddle: string | null;
  /** The registration that paddle resolves to, if any. */
  match: { approvalStatus: string; bidLimitCents: number | null } | null;
}

export interface SaleWarning {
  code: 'unknown_paddle' | 'not_approved' | 'over_limit' | 'price_mismatch' | 'no_buyer';
  message: string;
}

/**
 * Things worth saying out loud before a horse changes hands.
 *
 * These are WARNINGS, never blocks (brief section 39: "do not silently block
 * legitimate edge cases; escalate clearly"). Every one of them has a real
 * auction in which it is correct: a bidder approved on paper seconds ago and
 * not yet in the system, a limit the office agreed to raise at the rail, a
 * price the auctioneer genuinely took below the board after a retraction. The
 * clerk is standing in the room and we are not, so the console says what it
 * noticed and lets a person decide.
 *
 * Ordered by how much they cost if ignored: an unidentifiable buyer outranks a
 * surprising number, because money with no name attached is the one nobody can
 * unpick afterwards.
 */
export function saleWarnings(input: SaleCheckInput): SaleWarning[] {
  const out: SaleWarning[] = [];
  if (!input.paddle) {
    out.push({ code: 'no_buyer', message: 'No buyer number. The office will have to attribute this sale at checkout.' });
  } else if (!input.match) {
    out.push({ code: 'unknown_paddle', message: `No bidder holds paddle ${input.paddle}.` });
  } else {
    if (input.match.approvalStatus !== 'APPROVED') {
      out.push({ code: 'not_approved', message: `Paddle ${input.paddle} is ${input.match.approvalStatus}, not approved to bid.` });
    }
    const limit = input.match.bidLimitCents;
    if (limit != null && input.price * 100 > limit) {
      out.push({ code: 'over_limit', message: `Over this bidder's limit of $${Math.round(limit / 100).toLocaleString()}.` });
    }
  }
  // The board is the record of what was bid. Selling at a different number is
  // legitimate but is worth a second look before it becomes a settlement fact.
  if (input.currentHighBid !== null && input.price !== input.currentHighBid) {
    out.push({ code: 'price_mismatch', message: `Board shows $${input.currentHighBid.toLocaleString()}, not $${input.price.toLocaleString()}.` });
  }
  return out;
}

/**
 * "Buck - 7yo Quarter Horse gelding" -> name "Buck", descriptor the rest.
 *
 * The catalog stores one title string, but the NAME is what the clerk says
 * out loud and it deserves its own weight. Split on a dash used as a
 * separator (spaced, any of hyphen / en / em), which is the convention the
 * catalogs use.
 *
 * A title with no separator is ALL NAME. That is the safe direction to be
 * wrong in: showing the whole title large is merely less pretty, whereas
 * guessing a split point would put half a horse's registered name in small
 * grey type.
 */
export function splitTitleFor(title: string | null): { name: string | null; descriptor: string | null } {
  if (!title) return { name: null, descriptor: null };
  const m = /^(.+?)\s+[-‐-―]\s+(.+)$/.exec(title.trim());
  if (!m) return { name: title.trim(), descriptor: null };
  return { name: m[1]!.trim(), descriptor: m[2]!.trim() };
}

export interface PendingSeller {
  name: string;
  phone: string | null;
  needsOfficeDetail: boolean;
}
export interface SaleBidderRequirements {
  cardOnFile?: { required?: boolean } | undefined;
  deposit?: { required?: boolean; amountCents?: number | null } | undefined;
  bankLetter?: { required?: boolean } | undefined;
}

/**
 * What is still missing before this walk-up seller can be handed a bidder
 * card.
 *
 * The barn crew takes a horse in at the gate with whatever the person in front
 * of them can produce. The buyer desk is where a paddle is issued, and by then
 * somebody needs to know what was NOT collected - otherwise the desk finds out
 * halfway through printing, with the seller standing there.
 *
 * Everything here is derived from records that already exist: the consignor
 * row the gate created, and the sale's own bidder financial requirements
 * (migration 0033). Nothing is invented, because a checklist of made-up
 * prerequisites is worse than no checklist - staff learn to ignore it.
 *
 * These are things to TELL somebody, not gates to enforce. The desk can still
 * issue a card; a sale that genuinely blocks on a deposit enforces that in
 * approval, not in a hint on a queue row.
 */
export function bidderCardBlockers(seller: PendingSeller, req: SaleBidderRequirements | null | undefined): string[] {
  const out: string[] = [];
  if (!seller.name.trim()) out.push('No name recorded');
  if (!seller.phone) out.push('No contact number on file');
  if (seller.needsOfficeDetail) out.push('Office needs tax details before payout');
  if (req?.cardOnFile?.required) out.push('This sale requires a card on file');
  if (req?.deposit?.required) {
    const amt = req.deposit.amountCents;
    out.push(amt ? `This sale requires a $${Math.round(amt / 100).toLocaleString()} deposit` : 'This sale requires a deposit');
  }
  if (req?.bankLetter?.required) out.push('This sale requires a bank letter of credit');
  return out;
}
